import axios from 'axios'

import { api } from './client'

export const authApi = {
  // FastAPI's OAuth2 form login expects urlencoded username/password.
  login: async (email, password, recaptchaToken) => {
    const form = new URLSearchParams()
    form.append('username', email)
    form.append('password', password)
    // Google reCAPTCHA's answer, when the login page shows it.
    if (recaptchaToken) form.append('recaptcha_token', recaptchaToken)
    const { data } = await api.post('/auth/login', form, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    })
    return data
  },
  /** { enabled, site_key }: whether login needs reCAPTCHA. Public. */
  captcha: async () => (await api.get('/auth/captcha')).data,
  me: async () => (await api.get('/auth/me')).data,
  /** What the signed-in role may do, with the sentence explaining it. */
  permissions: async () => (await api.get('/auth/permissions')).data,
  changePassword: async (currentPassword, newPassword) =>
    (
      await api.post('/auth/password', {
        current_password: currentPassword,
        new_password: newPassword,
      })
    ).data,
}

export const usersApi = {
  list: async (params = {}) => (await api.get('/users', { params })).data,
  get: async (id) => (await api.get(`/users/${id}`)).data,
  /** Role catalogue with each role's permission list — drives the role picker. */
  roles: async () => (await api.get('/users/roles')).data,
  create: async (payload) => (await api.post('/users', payload)).data,
  update: async (id, payload) => (await api.patch(`/users/${id}`, payload)).data,
  /** Admin setting someone else's password. */
  setPassword: async (id, newPassword) =>
    (await api.post(`/users/${id}/password`, { new_password: newPassword })).data,
  /** Soft delete: the account is disabled, its history stays. */
  deactivate: async (id) => (await api.delete(`/users/${id}`)).data,
}

/** Roles and permissions. Reading needs roles:read; editing is super-admin only. */
export const rbacApi = {
  roles: async () => (await api.get('/rbac/roles')).data,
  permissions: async () => (await api.get('/rbac/permissions')).data,
  createRole: async (payload) => (await api.post('/rbac/roles', payload)).data,
  updateRole: async (id, payload) => (await api.patch(`/rbac/roles/${id}`, payload)).data,
  deleteRole: async (id) => (await api.delete(`/rbac/roles/${id}`)).data,
  /** The matrix screen submits a role's whole grant set, not a diff. */
  setRolePermissions: async (id, permissions) =>
    (await api.put(`/rbac/roles/${id}/permissions`, { permissions })).data,
  createPermission: async (payload) => (await api.post('/rbac/permissions', payload)).data,
}

// The browser's timezone, sent with a download so the server prints the
// file's times the way this screen shows them.
const viewerZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined
  } catch {
    return undefined
  }
}

export const ordersApi = {
  list: async (params = {}) => (await api.get('/orders', { params })).data,
  get: async (id) => (await api.get(`/orders/${id}`)).data,
  create: async (payload) => (await api.post('/orders', payload)).data,
  addItem: async (orderId, payload) =>
    (await api.post(`/orders/${orderId}/items`, payload)).data,
  updateItem: async (orderId, itemId, payload) =>
    (await api.patch(`/orders/${orderId}/items/${itemId}`, payload)).data,
  deleteItem: async (orderId, itemId) =>
    (await api.delete(`/orders/${orderId}/items/${itemId}`)).data,
  /** Accept a line below its category's minimum order; recorded as yours. */
  waiveMinimum: async (orderId, itemId) =>
    (await api.post(`/orders/${orderId}/items/${itemId}/waive-minimum`)).data,
  undoWaiveMinimum: async (orderId, itemId) =>
    (await api.delete(`/orders/${orderId}/items/${itemId}/waive-minimum`)).data,
  setStatus: async (id, payload) => (await api.post(`/orders/${id}/status`, payload)).data,
  /** The order's transport service and LR / reference number. */
  setTransport: async (id, payload) =>
    (await api.patch(`/orders/${id}/transport`, payload)).data,
  /** The date to deliver by; null clears it. */
  setDeliveryDate: async (id, date) =>
    (await api.patch(`/orders/${id}/delivery-date`, { requested_delivery_date: date })).data,

  /** 'delivery' or 'pickup' (collected from the store). */
  setFulfilment: async (id, fulfilment) =>
    (await api.patch(`/orders/${id}/fulfilment`, { fulfilment })).data,

  /** Send this order to one of the customer's saved addresses. */
  setShippingAddress: async (id, addressId) =>
    (await api.patch(`/orders/${id}/shipping-address`, { address_id: addressId })).data,

  /**
   * One order as a printable sheet — the copy that goes to the driver, the
   * customer, and whoever settles the argument afterwards.
   */
  pdf: async (id) => {
    const response = await api.get(`/orders/${id}/pdf`, {
      params: { tz: viewerZone() },
      responseType: 'blob',
    })
    const disposition = response.headers['content-disposition'] ?? ''
    const named = /filename="?([^"]+)"?/.exec(disposition)
    return { blob: response.data, filename: named ? named[1] : `order-${id}.pdf` }
  },

  /** Send part of the order: { items: [{ order_item_id, quantity }], transport_reference }. */
  ship: async (id, payload) => (await api.post(`/orders/${id}/shipments`, payload)).data,

  /** One shipment arrived. */
  deliverShipment: async (id, shipmentId) =>
    (await api.post(`/orders/${id}/shipments/${shipmentId}/delivered`)).data,

  /** Correct a wrong invoice number, with the reason. Needs invoices:correct. */
  correctInvoiceNumber: async (invoiceId, number, reason) =>
    (await api.patch(`/invoices/${invoiceId}/number`, { number, reason })).data,

  /** An issued invoice, drawn from what was saved when it was issued. */
  invoicePdf: async (invoiceId) => {
    const response = await api.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' })
    const disposition = response.headers['content-disposition'] ?? ''
    const named = /filename="?([^"]+)"?/.exec(disposition)
    return { blob: response.data, filename: named ? named[1] : `invoice-${invoiceId}.pdf` }
  },

  /**
   * Download the filtered orders as a file.
   *
   * Fetched rather than linked: the API wants an Authorization header, and
   * a plain <a href> cannot carry one. The server names the file, so the
   * name matches the filters that produced it.
   */
  /** Confirmed and packed orders, grouped by transport service. */
  transportLoads: async (params = {}) =>
    (await api.get('/orders/transport-loads', { params: { ...params, tz: viewerZone() } })).data,
  exportTransportLoads: async (params, format) =>
    namedFile(
      await api.get('/orders/transport-loads/export', {
        params: { ...params, format, tz: viewerZone() },
        responseType: 'blob',
      }),
      `transport-report.${format}`,
    ),
  export: async (params, format) => {
    const response = await api.get('/orders/export', {
      params: { ...params, format, tz: viewerZone() },
      responseType: 'blob',
    })
    const disposition = response.headers['content-disposition'] ?? ''
    const named = /filename="?([^"]+)"?/.exec(disposition)
    return {
      blob: response.data,
      filename: named ? named[1] : `orders.${format}`,
    }
  },
}

export const productsApi = {
  list: async (params = {}) => (await api.get('/products', { params })).data,
  get: async (id) => (await api.get(`/products/${id}`)).data,
  categories: async () => (await api.get('/products/categories')).data,
  create: async (payload) => (await api.post('/products', payload)).data,
  update: async (id, payload) => (await api.patch(`/products/${id}`, payload)).data,
  /** Only a product no order has ever named; the API refuses the rest. */
  remove: async (id) => (await api.delete(`/products/${id}`)).data,
  /** Try the matcher against arbitrary text — used by the Match Tester page. */
  matchTest: async (q) => (await api.get('/products/match-test', { params: { q } })).data,
  addAlias: async (id, alias) =>
    (await api.post(`/products/${id}/aliases`, null, { params: { alias } })).data,
  /** The filtered catalog as a file ('csv' | 'xlsx'), in the layout import reads. */
  export: async (params, format) => namedFile(
    await api.get('/products/export', { params: { ...params, format }, responseType: 'blob' }),
    `products.${format}`,
  ),
  /** An example file: the headings import expects and two filled rows. */
  sample: async (format) => namedFile(
    await api.get('/products/import/sample', { params: { format }, responseType: 'blob' }),
    `products-sample.${format}`,
  ),
  /**
   * Create or update products from a file, keyed on item code. Resolves to
   * { created, updated }; a file with bad rows rejects with a 422 whose
   * detail lists them, and nothing is written.
   */
  import: async (file) => {
    const form = new FormData()
    form.append('file', file)
    // Named explicitly: the client defaults to JSON, and axios turns a
    // FormData into JSON under that header. Given multipart, it hands the
    // body to the browser, which adds the boundary.
    return (
      await api.post('/products/import', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    ).data
  },
}

/** A blob response and the name the server gave it. */
function namedFile(response, fallback) {
  const disposition = response.headers['content-disposition'] ?? ''
  const named = /filename="?([^"]+)"?/.exec(disposition)
  return { blob: response.data, filename: named ? named[1] : fallback }
}

/**
 * Category management. `productsApi.categories` stays as it is — it returns
 * plain names for the Products filter, and this returns rows with ids and
 * product counts for the screen that edits them.
 */
/** States and their cities, for address pickers; managed in Settings. */
export const locationsApi = {
  states: async () => (await api.get('/locations/states')).data,
  createState: async (payload) => (await api.post('/locations/states', payload)).data,
  updateState: async (id, payload) => (await api.patch(`/locations/states/${id}`, payload)).data,
  removeState: async (id) => (await api.delete(`/locations/states/${id}`)).data,
  cities: async (stateId) =>
    (await api.get('/locations/cities', { params: stateId ? { state_id: stateId } : {} })).data,
  createCity: async (payload) => (await api.post('/locations/cities', payload)).data,
  updateCity: async (id, payload) => (await api.patch(`/locations/cities/${id}`, payload)).data,
  removeCity: async (id) => (await api.delete(`/locations/cities/${id}`)).data,
}

export const categoriesApi = {
  list: async () => (await api.get('/categories')).data,
  create: async (payload) => (await api.post('/categories', payload)).data,
  update: async (id, payload) => (await api.patch(`/categories/${id}`, payload)).data,
  remove: async (id) => (await api.delete(`/categories/${id}`)).data,
}

/**
 * Units of measure. A managed vocabulary only: a product still carries its
 * unit as text, so nothing here reprices anything.
 */
/** Transport services, kept by the shop and added to by customers. */
export const transportApi = {
  list: async (params = {}) => (await api.get('/transport-services', { params })).data,
  create: async (payload) => (await api.post('/transport-services', payload)).data,
  update: async (id, payload) => (await api.patch(`/transport-services/${id}`, payload)).data,
  remove: async (id) => (await api.delete(`/transport-services/${id}`)).data,
  get: async (id) => (await api.get(`/transport-services/${id}`)).data,
  /** Routes: { name, stops: [{ city, state }] }. Each returns the service. */
  addRoute: async (id, route) =>
    (await api.post(`/transport-services/${id}/routes`, route)).data,
  updateRoute: async (id, routeId, route) =>
    (await api.put(`/transport-services/${id}/routes/${routeId}`, route)).data,
  removeRoute: async (id, routeId) =>
    (await api.delete(`/transport-services/${id}/routes/${routeId}`)).data,
}

export const unitsApi = {
  list: async (params = {}) => (await api.get('/units', { params })).data,
  create: async (payload) => (await api.post('/units', payload)).data,
  update: async (id, payload) => (await api.patch(`/units/${id}`, payload)).data,
  remove: async (id) => (await api.delete(`/units/${id}`)).data,
}

/**
 * Tamil for a catalog name, suggested by the model.
 *
 * A suggestion only — it saves nothing. The admin reads it, corrects it, and
 * it is stored when they save the unit, category or product, because these
 * names are read back to customers over WhatsApp.
 */
export const translateApi = {
  suggest: async (text, entityType) =>
    (await api.post('/translate', { text, locale: 'ta', entity_type: entityType })).data,
}

/**
 * Bulk Tamil translation. Starting a job returns at once — the worker does
 * the work and the screen watches it, because a catalog of any size cannot
 * be translated inside a request.
 */
export const translationJobsApi = {
  /** How many rows of each kind have no Tamil yet. */
  pending: async () => (await api.get('/translation-jobs/pending')).data,
  /** `entityIds` omitted means every pending row of that kind. */
  start: async (entityType, entityIds) =>
    (
      await api.post('/translation-jobs', {
        entity_type: entityType,
        entity_ids: entityIds ?? undefined,
      })
    ).data,
  get: async (id) => (await api.get(`/translation-jobs/${id}`)).data,
  items: async (id) => (await api.get(`/translation-jobs/${id}/items`)).data,
  /** `approvals` is [{ item_id, value }] — the value as the admin left it. */
  approve: async (id, approvals) =>
    (await api.post(`/translation-jobs/${id}/approve`, { approvals })).data,
}
/**
 * What a unit weighs in each category — this one does reprice, which is the
 * point: without it a line ordered in rolls or bundles cannot be converted to
 * kilograms and goes to the review queue.
 *
 * Saved a row at a time, which is how the screen is edited. One request per
 * row also means a row cannot end up half-saved. A weight of null clears that
 * cell back to the catalog-wide default. The whole grid comes back, because a
 * cell does not know whether a weight was already stored for it.
 */
export const categoryUnitsApi = {
  list: async () => (await api.get('/category-units')).data,
  saveRow: async (categoryId, weights) =>
    (await api.put(`/category-units/${categoryId}`, { weights })).data,
}

export const customersApi = {
  list: async (params = {}) => (await api.get('/customers', { params })).data,
  get: async (id) => (await api.get(`/customers/${id}`)).data,
  create: async (payload) => (await api.post('/customers', payload)).data,
  update: async (id, payload) => (await api.patch(`/customers/${id}`, payload)).data,
  addContact: async (id, phone, label) =>
    (await api.post(`/customers/${id}/contacts`, null, { params: { phone, label } })).data,
  /** Shipping addresses. Each call returns all of them, default first. */
  addAddress: async (id, fields) => (await api.post(`/customers/${id}/addresses`, fields)).data,
  updateAddress: async (id, addressId, fields) =>
    (await api.patch(`/customers/${id}/addresses/${addressId}`, fields)).data,
  removeAddress: async (id, addressId) =>
    (await api.delete(`/customers/${id}/addresses/${addressId}`)).data,
}

/** Conversations: one per order, with the messages that made it. */
export const conversationsApi = {
  list: async (params = {}) => (await api.get('/conversations', { params })).data,
  get: async (key) => (await api.get(`/conversations/${key}`)).data,
}

export const messagesApi = {
  list: async (params = {}) => (await api.get('/messages', { params })).data,
  reprocess: async (id) => (await api.post(`/messages/${id}/reprocess`)).data,
  mediaUrl: (id) => `${import.meta.env.VITE_API_BASE_URL ?? ''}/api/v1/messages/${id}/media`,
  /**
   * The media endpoint is behind Bearer auth, and <img>/<audio> tags cannot
   * send an Authorization header — the browser just gets a 401 and the player
   * sits at 0:00. So fetch the bytes through the authenticated client and
   * hand back an object URL the tag can use. Callers must revoke it.
   */
  mediaBlobUrl: async (id) => {
    const { data } = await api.get(`/messages/${id}/media`, {
      responseType: 'blob',
    })
    return URL.createObjectURL(data)
  },
}

export const playgroundApi = {
  /** Dry-run one file through OCR/STT → extraction → matching. Saves nothing. */
  extract: async (file, params = {}) => {
    const form = new FormData()
    form.append('file', file)
    const { data } = await api.post('/playground/extract', form, {
      params,
      // Let the browser set the multipart boundary; overriding it breaks the upload.
      headers: { 'Content-Type': undefined },
      // Local OCR and Whisper are slow on first call while models load —
      // large-v3 has to download several GB before it can transcribe.
      timeout: 1_800_000,
    })
    return data
  },
}

export const aiCallsApi = {
  /** Audit log of every AI provider request. */
  list: async (params = {}) => (await api.get('/ai-calls', { params })).data,
  get: async (id) => (await api.get(`/ai-calls/${id}`)).data,
  stats: async (params = {}) => (await api.get('/ai-calls/stats', { params })).data,
  /** Credit left with each provider, and what we have spent with it. */
  balances: async () => (await api.get('/ai-calls/balances')).data,
  /** Calls and cost over today / 7 days / 30 days / all time, in one go. */
  usage: async () => (await api.get('/ai-calls/usage')).data,
}

export const reportsApi = {
  dashboard: async () => (await api.get('/reports/dashboard')).data,
  sales: async (params = {}) => (await api.get('/reports/sales', { params })).data,
  topProducts: async (params = {}) => (await api.get('/reports/top-products', { params })).data,
  aiAccuracy: async (params = {}) => (await api.get('/reports/ai-accuracy', { params })).data,
  /** Sales by 'product', 'customer' or 'category', with totals. */
  salesBy: async (kind, params = {}) =>
    (await api.get(`/reports/sales-by/${kind}`, { params })).data,
  exportSalesBy: async (kind, params, format) =>
    namedFile(
      await api.get(`/reports/sales-by/${kind}/export`, {
        params: { ...params, format },
        responseType: 'blob',
      }),
      `${kind}-sales.${format}`,
    ),
}

/** The shop's logos. Reading is public — the login page shows one. */
export const brandingApi = {
  get: async () => (await api.get('/branding')).data,
  /** kind is 'site' or 'invoice'. Needs settings:write. */
  upload: async (kind, file) => {
    const form = new FormData()
    form.append('file', file)
    const { data } = await api.put(`/branding/logo/${kind}`, form, {
      // Let the browser set the multipart boundary; overriding it breaks the upload.
      headers: { 'Content-Type': undefined },
    })
    return data
  },
  /** Back to the bundled artwork. */
  reset: async (kind) => (await api.delete(`/branding/logo/${kind}`)).data,
}

export const settingsApi = {
  /** Values in force, plus the field definitions the settings screen renders. */
  get: async () => (await api.get('/settings')).data,
  /** Admin only. Takes [{key, value}]; returns the settings as they now stand. */
  update: async (updates) => (await api.put('/settings', updates)).data,
  /** Asks each outside service whether it is working. Real calls; nothing
   *  is sent to a customer. */
  check: async () => (await api.post('/settings/check')).data,
  /** Send one email with the saved mail account; returns { ok, detail }. */
  testEmail: async (to) => (await api.post('/settings/email/test', { to: to || '' })).data,
}

/** The AI services this shop has connected. A provider knows its own
 *  address, so connecting one is a name and a key. */
export const providersApi = {
  list: async () => (await api.get('/settings/providers')).data,
  connect: async (code, apiKey) =>
    (await api.put(`/settings/providers/${code}`, { api_key: apiKey })).data,
  disconnect: async (code) => (await api.delete(`/settings/providers/${code}`)).data,
  check: async (code) => (await api.post(`/settings/providers/${code}/check`)).data,
}

/**
 * The customer's own order page, opened from a WhatsApp link.
 *
 * Deliberately not on the shared `api` client. That one attaches the staff
 * bearer token and, on a 401, clears it and redirects to /login — neither of
 * which belongs on a page whose whole point is that the visitor has no
 * account. An operator who happened to be signed in would otherwise send
 * their own credentials along with a customer's link, and a customer whose
 * link had expired would be dropped at a login screen they can never pass.
 *
 * The token in the path is the only credential, so there is nothing to
 * attach and nothing to refresh.
 */
const publicApi = axios.create({
  baseURL: `${import.meta.env.VITE_API_BASE_URL ?? ''}/api/v1`,
  headers: { 'Content-Type': 'application/json' },
})

export const orderLinkApi = {
  get: async (token) => (await publicApi.get(`/order-link/${token}`)).data,

  /**
   * Where the order's photo or voice note is served: kind is 'image' or
   * 'audio'. A plain URL, not a request — the token in it is the only
   * credential, so <img> and <audio> can load it directly.
   */
  mediaUrl: (token, kind) =>
    `${import.meta.env.VITE_API_BASE_URL ?? ''}/api/v1/order-link/${token}/media/${kind}`,

  searchProducts: async (token, q) =>
    (await publicApi.get(`/order-link/${token}/products`, { params: { q } })).data,

  /**
   * Every product in one category, or the whole catalog with no category.
   * Browsing, not searching: nothing is truncated at the search limit.
   */
  productsIn: async (token, categoryId) =>
    (
      await publicApi.get(`/order-link/${token}/products`, {
        params: categoryId == null ? {} : { category_id: categoryId },
      })
    ).data,

  updateLine: async (token, lineId, changes) =>
    (await publicApi.patch(`/order-link/${token}/items/${lineId}`, changes)).data,

  removeLine: async (token, lineId) =>
    (await publicApi.delete(`/order-link/${token}/items/${lineId}`)).data,

  addLine: async (token, line) =>
    (await publicApi.post(`/order-link/${token}/items`, line)).data,

  /** Sends the code. Confirms nothing on its own. */
  requestCode: async (token) =>
    (await publicApi.post(`/order-link/${token}/confirm`)).data,

  /** The code coming back is what actually confirms the order. */
  verify: async (token, code) =>
    (await publicApi.post(`/order-link/${token}/verify`, { code })).data,

  /** 'Some Items Not Delivered': which lines (optional) and a note. Returns the order. */
  reportDeliveryIssue: async (token, report) =>
    (await publicApi.post(`/order-link/${token}/report-delivery-issue`, report)).data,

  /** 'All Items Are Delivered' on a delivered order. Returns the order. */
  confirmDelivery: async (token) =>
    (await publicApi.post(`/order-link/${token}/confirm-delivery`)).data,

  /** The managed city list ({ name, state }), for adding a transport service. */
  cities: async (token) => (await publicApi.get(`/order-link/${token}/cities`)).data,

  /** Active transport services in the customer's city. */
  transportServices: async (token, params = {}) =>
    (await publicApi.get(`/order-link/${token}/transport-services`, { params })).data,

  /** Choose one for this order, or null to clear. Returns the order. */
  chooseTransport: async (token, transportServiceId) =>
    (await publicApi.put(`/order-link/${token}/transport`, {
      transport_service_id: transportServiceId,
    })).data,

  /** Add a service that was not listed; it is chosen too. Returns the order. */
  addTransport: async (token, service) =>
    (await publicApi.post(`/order-link/${token}/transport-services`, service)).data,

  /** Send this order to one of their saved addresses. Returns the order. */
  chooseShippingAddress: async (token, addressId) =>
    (await publicApi.put(`/order-link/${token}/shipping-address`, { address_id: addressId })).data,

  /** The date they would like it by, or null to clear. Returns the order. */
  setDeliveryDate: async (token, date) =>
    (await publicApi.put(`/order-link/${token}/delivery-date`, {
      requested_delivery_date: date,
    })).data,

  /** 'delivery' or 'pickup' (collected from the store). */
  setFulfilment: async (token, fulfilment) =>
    (await publicApi.put(`/order-link/${token}/fulfilment`, { fulfilment })).data,

  /** Save a new delivery address and send this order there. Returns the order. */
  addShippingAddress: async (token, address) =>
    (await publicApi.post(`/order-link/${token}/shipping-addresses`, address)).data,
}
